import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CreateDocumentRecord } from '@/models/DocumentRecord';
import {
  createDocumentRecord,
  deleteDocumentRecord,
  generateDocumentRecordUrl,
  getPropertyDocumentRecords,
  updateDocumentRecord,
} from '@/services/api/document-records';

export const useDocumentRecords = (propertyId?: string) => {
  const queryClient = useQueryClient();

  const { data: documentRecords, isFetching: isFetchingDocumentRecords } = useQuery({
    queryKey: ['document-records', propertyId],
    queryFn: () => getPropertyDocumentRecords(propertyId),
    enabled: Boolean(propertyId),
  });

  const {
    mutate: createDocumentRecordRequest,
    isPending: isCreatingDocumentRecord,
    isSuccess: isDocumentRecordCreated,
    error: createDocumentRecordError,
  } = useMutation({
    mutationFn: createDocumentRecord,
    onSuccess: ({ propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['document-records', propertyId] });
    },
  });

  const {
    mutate: deleteDocumentRecordRequest,
    isPending: isDeletingDocumentRecord,
    isSuccess: isDocumentRecordDeleted,
    error: deleteDocumentRecordError,
  } = useMutation({
    mutationFn: ({ id, propertyId: _propertyId }: { id: string; propertyId?: string }) =>
      deleteDocumentRecord({ id }),
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['document-records', propertyId] });
    },
  });

  const { mutate: updateDocumentRecordRequest, isPending: isUpdatingDocumentRecord } = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      updateDocumentRecord({ id, data: { name } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-records', propertyId] });
    },
  });

  const {
    mutate: generateDocumentRecordUrlRequest,
    data: generatedDocumentRecordUrl,
    isPending: isGeneratingDocumentRecordUrl,
    isSuccess: isDocumentRecordUrlGenerated,
    error: generateDocumentRecordUrlError,
  } = useMutation({
    mutationFn: generateDocumentRecordUrl,
  });

  return {
    documentRecords,
    isFetchingDocumentRecords,

    createDocumentRecord: (data: CreateDocumentRecord) =>
      propertyId && createDocumentRecordRequest({ propertyId, data }),
    isCreatingDocumentRecord,
    isDocumentRecordCreated,
    createDocumentRecordError,

    renameDocumentRecord: (id: string, name: string) => updateDocumentRecordRequest({ id, name }),
    isUpdatingDocumentRecord,

    deleteDocumentRecord: (id: string) => deleteDocumentRecordRequest({ id, propertyId }),
    isDeletingDocumentRecord,
    isDocumentRecordDeleted,
    deleteDocumentRecordError,

    generateDocumentRecordUrl: (id: string) => generateDocumentRecordUrlRequest({ id }),
    generatedDocumentRecordUrl,
    isGeneratingDocumentRecordUrl,
    isDocumentRecordUrlGenerated,
    generateDocumentRecordUrlError,
  };
};
